import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { isNil } from 'lodash-es';
import { merge, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { ReadyPipe } from 'src/app/operational-data-common/feature-pipes/ready.pipe';

import {
  DcSensorValueProperties,
  DeviceIds,
  Feature,
  FeatureDeprecated,
  FeatureNames,
  FeaturePropertyKey,
  FlowLimitTemperatureCommands,
  FlowLimitTemperatureProperties,
  HeatingCompressorsProperties,
  HeatingCurveCommands,
  HeatingCurveProperties,
  IsNumberValueProperty,
} from '@vi-iot/client';

import { DevicesStore, InstallationFeaturesStore } from '@store';
import { NO_VALUE } from '@pipes';

import { GTMService } from '../../../../../shared/services';
import { HeatingCircuitIgnoredModalService } from '../../../heating-circuit-ignored-modal/heating-circuit-ignored-modal.service';
import { InstallationService } from 'src/app/+installations/services/installation.service';

@Component({
  selector: 'app-heat-curve-panel',
  templateUrl: './heat-curve-panel.component.html',
  styleUrls: ['./heat-curve-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeatCurvePanelComponent implements OnInit, OnDestroy {
  @Input() circuitId: string;

  readonly featurePropertyKey = FeaturePropertyKey;
  readonly NO_VALUE = NO_VALUE;

  isE3HeatPumpDevice$: Observable<boolean>;
  operatingProgram$: Observable<FeatureDeprecated>; // strange, never assigned, probably not working. leaving. TODO
  operatingProgramNormalTemperature$: Observable<number>;
  heatingCurve$: Observable<Feature<HeatingCurveProperties, HeatingCurveCommands>>;
  flowTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  flowLimitTemperature$: Observable<
    Feature<FlowLimitTemperatureProperties, FlowLimitTemperatureCommands>
  >;
  compressorStatus$: Observable<Feature<HeatingCompressorsProperties, never>>;
  shiftValue$ = new Observable<{ value: number }>();
  slopeValue$ = new Observable<{ value: number }>();
  setHeatingCurveShiftValue$ = new Subject<number>();
  setHeatingCurveSlopeValue$ = new Subject<number>();

  heatingCurveWidth: number;
  flowLimitTemperature: Feature<FlowLimitTemperatureProperties, FlowLimitTemperatureCommands>;
  isHeatingCircuitWithActiveSRCAndDevices$: Observable<boolean>;
  currentFlowTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  targetFlowTemperature$: Observable<Feature<IsNumberValueProperty, never>>;
  isTargetFlowTemperatureVisible$: Observable<boolean>;
  calculatedOutsideTemperature$: Observable<Feature<IsNumberValueProperty, never>>;
  outsideTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  deviceSensorsTemperature$: Observable<Feature<DcSensorValueProperties, never>>;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private heatingCircuitIgnoredModalService: HeatingCircuitIgnoredModalService,
    private gtmService: GTMService,
    private readyPipe: ReadyPipe,
    private devicesStore: DevicesStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private installationService: InstallationService
  ) {}

  ngOnInit(): void {
    this.isE3HeatPumpDevice$ = this.devicesStore.isE3HeatpumpDevice();

    this.heatingCurve$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitHeatingCurve(this.circuitId)
    );
    this.flowTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitSupplyTemperature(this.circuitId)
    );

    this.flowLimitTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.flowLimitTemperature(this.circuitId)
    );
    this.compressorStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.compressorStatus
    );
    this.operatingProgramNormalTemperature$ = this.devicesStore.getNormalProgramTemperature(
      this.circuitId
    );

    this.isHeatingCircuitWithActiveSRCAndDevices$ = this.devicesStore.isAnySRCDeviceAssignedToHeatingCircuit(
      Number(this.circuitId),
      DeviceIds.RoomControl
    );

    this.outsideTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.outsideTemperature
    );

    this.deviceSensorsTemperature$ = this.devicesStore.vitotrolConnectedToGateway$.pipe(
      filter((device) => !!device),
      switchMap((device) =>
        this.installationFeaturesStore.selectInstallationDeviceFeature<
          DcSensorValueProperties,
          never
        >(FeatureNames.deviceTemperatureSensor, device.id)
      )
    );

    this.devicesStore.selectedDevice$
      .pipe(
        filter((device) => !!device),
        take(1),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((device) => {
        this.runSubscriptionsAfterGettingDeviceId(device.id, device.gatewaySerial);
      });

    this.initHeatCurveValueSubjects();
    this.initFlowLimitTemperature();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onShiftChange(value: number, oldValue: number): void {
    this.heatingCircuitIgnoredModalService
      .showModalWhenSrcIsActive()
      .pipe(take(1))
      .subscribe((updateValue) => {
        if (updateValue) {
          this.installationFeaturesStore.updateFeature(
            FeatureNames.circuitHeatingCurve(this.circuitId),
            value,
            FeaturePropertyKey.shift,
            'shift',
            'setCurve'
          );
        } else {
          this.setHeatingCurveShiftValue$.next(oldValue);
        }
      });

    this.gtmService.track('onSet', 'Heat curve shift', 'hcManager');
  }

  onSlopeChange(newValue: number, oldValue: number): void {
    this.heatingCircuitIgnoredModalService
      .showModalWhenSrcIsActive()
      .pipe(take(1))
      .subscribe((updateValue) => {
        if (updateValue) {
          this.installationFeaturesStore.updateFeature(
            FeatureNames.circuitHeatingCurve(this.circuitId),
            newValue,
            FeaturePropertyKey.slope,
            'slope',
            'setCurve'
          );
        } else {
          this.setHeatingCurveSlopeValue$.next(oldValue);
        }
      });

    this.gtmService.track('onSet', 'Heat curve slope', 'hcManager');
  }

  private initHeatCurveValueSubjects(): void {
    this.shiftValue$ = merge(
      this.setHeatingCurveShiftValue$,
      this.heatingCurve$.pipe(
        takeUntil(this.ngUnsubscribe),
        filter((feature) => !!feature.properties && !isNil(feature.properties['shift'])),
        map((feature) => feature.properties['shift'].value)
      )
    ).pipe(
      takeUntil(this.ngUnsubscribe),
      map((value) => ({ value }))
    );

    this.slopeValue$ = merge(
      this.setHeatingCurveSlopeValue$,
      this.heatingCurve$.pipe(
        takeUntil(this.ngUnsubscribe),
        filter((feature) => !!feature.properties && !isNil(feature.properties['slope'])),
        map((feature) => feature.properties['slope'].value)
      )
    ).pipe(
      takeUntil(this.ngUnsubscribe),
      map((value) => ({ value }))
    );
  }

  private initFlowLimitTemperature(): void {
    this.flowLimitTemperature$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((feature) => !!this.readyPipe.transform(feature))
      )
      .subscribe((feature) => (this.flowLimitTemperature = feature));
  }

  private runSubscriptionsAfterGettingDeviceId(deviceId: string, gatewaySerial: string): void {
    this.currentFlowTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitSupplyTemperature(this.circuitId),
      deviceId
    );

    this.targetFlowTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsNumberValueProperty,
      never
    >(FeatureNames.circuitTemperature(this.circuitId), deviceId);

    this.isTargetFlowTemperatureVisible$ = this.installationService.isHeatingCircuitTargetFlowTemperatureVisible(
      this.circuitId,
      deviceId
    );

    this.calculatedOutsideTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.calculatedTemperatureOutside,
      deviceId,
      gatewaySerial
    );
  }
}
